import {
    action, computed, IReactionDisposer, makeObservable, observable, reaction,
} from 'mobx';

import { BASEURL, CancellableAPI, ENDPOINTS } from '@/api';
import i18n from '@/content';
import {
    captureErrorForSentry,
    checkIs404,
    checkIsPermissionsFailedError,
    downloadFile,
} from '@/components/utils';

import AuthSettingsStore from '../AuthSettingsStore';
import { AsyncDownloadItem, BatchDownload, StatusCheckWorker } from './interfaces';
import { getBatchDownloadStatus, getFileDownloadStatus } from './helpers';
import { DownloadActionResult } from '@/stores/FilesAccessStore/interfaces';
import { openFile } from '@/stores/FilesAccessStore';
import { OpenOptions } from '@/config/openOptions';

const CREATE_BATCH_KEY = '__CREATE_BATCH_KEY__';
const POLLING_INTERVAL_MS = 10_000;
const batchActionsNameSpace = 'filesTable.extraContent.batchActions';

class BatchDownloadStore {
    constructor(authSettingsStore: AuthSettingsStore) {
        this.API = new CancellableAPI(authSettingsStore, { failSilently: false, isPeriodic: true });
        makeObservable(this);
        this.watchOnDownloads();
        this.authSettingsStore = authSettingsStore;
    }

    private readonly API: CancellableAPI;

    private readonly processManagementMap: Map<string, NodeJS.Timeout> = new Map<string, NodeJS.Timeout>();

    private downloadsMapReaction: IReactionDisposer;

    private readonly authSettingsStore: AuthSettingsStore;

    @observable downloadsMap: Map<string, AsyncDownloadItem> = new Map<string, AsyncDownloadItem>();

    @observable isWidgetOpen = false;

    @observable MFARequiredFilesList: string[] = [];

    @observable isMFAMounted: boolean = false;

    @observable isMFAVisible: boolean = false;

    @observable isMFAPasswordMounted: boolean = false;

    @observable isMFAPasswordVisible: boolean = false;

    /**
     * Remembers where the last MFA requirement came from:
     * - 'batch'   → user initiated a real batch download (multiple files / Download All)
     * - 'single'  → user initiated a single-file download/open
     * Used by MFA modals to choose whether to restart batch download or keep single-file flow.
     */
    @observable lastMFASource: 'batch' | 'single' | null = null;

    @computed
    get downloadsList(): AsyncDownloadItem[] {
        return [...this.downloadsMap.values()];
    }

    @computed
    get MfaFilesList(): string[] {
        return [...this.MFARequiredFilesList];
    }

    @computed
    get downloadedItemsCount(): number {
        return [...this.downloadsMap.values()].filter((item) => item.status === 'DONE').length;
    }

    @action
    setMFARequiredFilesList = (value: string[]): void => {
        this.MFARequiredFilesList = value;
    };

    @action
    setLastMFASource = (value: 'batch' | 'single' | null): void => {
        this.lastMFASource = value;
    };

    @action
    setDownloadsMap = (downloadsMap: Map<string, AsyncDownloadItem>): void => {
        this.downloadsMap = downloadsMap;
    };

    @action
    setBatchDownload = (batchId: string, batchDownload: AsyncDownloadItem): void => {
        this.downloadsMap.set(batchId, batchDownload);
        this.setDownloadsMap(this.downloadsMap);
    };

    @action
    setIsProgressWidgetOpen = (value: boolean): void => {
        this.isWidgetOpen = value;
    };

    @action
    setIsMFAMounted = (value: boolean): void => {
        this.isMFAMounted = value;
    };

    @action
    setIsMFAVisible = (value: boolean): void => {
        this.isMFAVisible = value;
    };

    @action
    setIsMFAPasswordMounted = (value: boolean): void => {
        this.isMFAPasswordMounted = value;
    };

    @action
    setIsMFAPasswordVisible = (value: boolean): void => {
        this.isMFAPasswordVisible = value;
    };

    @action
    clear = (): void => {
        this.downloadsMap.clear();
        this.processManagementMap.forEach((timeout) => clearTimeout(timeout));
        this.API.cancelAll();
        this.processManagementMap.clear();
    };

    cancel = (batchId: string): void => {
        this.API.cancelRequest(batchId);
        clearTimeout(this.processManagementMap.get(batchId));
        this.remove(batchId);
    };

    @action
    remove = (batchId: string): void => {
        this.processManagementMap.delete(batchId);
        this.downloadsMap.delete(batchId);
        this.setDownloadsMap(this.downloadsMap);
    };

    startBatchDownload = async (filesIds: string[], mfaCode?: string): Promise<void> => {
        const { API } = this;
        const { result } = await API.post<BatchDownload>(
            BASEURL.backend(),
            ENDPOINTS.startBatchDownload(),
            CREATE_BATCH_KEY, // Actually this request doesn't suppose to be cancellable
            { body: { files: filesIds, auth_code: mfaCode } },
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {
            batch_id: id, user_id: userId, files, ...rest
        } = result;
        this.setBatchDownload(result.batch_id, { id, ...rest });
        this.tryScheduleStatusCheck(result.batch_id, getBatchDownloadStatus, OpenOptions.download);
    };

    private tryScheduleStatusCheck(
        itemId: string,
        processStatusCheck: StatusCheckWorker,
        accessType: OpenOptions,
        failOn500Status = false,
        isProtected = false,
    ): void {
        if (this.downloadsMap.has(itemId)) {
            const scheduleTimeout = setTimeout(
                () => this.tryUpdateStatus(itemId, processStatusCheck, accessType, failOn500Status, isProtected),
                POLLING_INTERVAL_MS,
            );
            this.processManagementMap.set(itemId, scheduleTimeout);
        }
    }

    private processStatusCheckResult(itemId: string, result: AsyncDownloadItem, accessType: OpenOptions = OpenOptions.download): boolean {
        let shouldReschedule = true;
        if (this.downloadsMap.has(itemId)) {
            const { status, link, name } = result;
            this.setBatchDownload(itemId, result);
            shouldReschedule = status === 'PENDING' || status === 'PROCESSING' || status === 'FETCHING';
            if (status === 'DONE' && link) {
                if (accessType === OpenOptions.download) {
                    downloadFile(link, name);
                } else {
                    const newWin = window.open(link);
                    if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
                        window.location.href = link;
                    }
                }
            }
            shouldReschedule = status !== 'DONE' && status !== 'ERROR' && status !== 'DELETED';
        } else {
            shouldReschedule = false;
        }
        return shouldReschedule;
    }

    private processSuccessfulResult(itemId: string, result: DownloadActionResult): void {
        const { status, link } = result;
        if (this.downloadsMap.has(itemId) && status === 'success') {
            const filename = this.downloadsMap.get(itemId)?.name;
            downloadFile(link, filename);
            this.partialUpdateBatchDownload(itemId, { status: 'DONE' });
        }
    }

    private processStatusCheckError(itemId: string, error: unknown, failOn500Status: boolean, isProtected: boolean): boolean {
        let shouldReschedule = true;
        const { API } = this;
        if (API.checkIsCancel(error)) {
            shouldReschedule = false;
        } else {
            captureErrorForSentry(error, 'BatchDownloadStore.checkStatus');
            if (checkIs404(error)) {
                this.partialUpdateBatchDownload(itemId,
                    {
                        message: i18n.t('general.errors.404', { type: i18n.t('general.specterxCommon.file') }),
                        status: 'ERROR',
                    });
                shouldReschedule = false;
            } else if (checkIsPermissionsFailedError(error)) {
                this.partialUpdateBatchDownload(itemId,
                    {
                        message: i18n.t(`${batchActionsNameSpace}.downloadPermissionFailed`),
                        status: 'ERROR',
                    });
                shouldReschedule = false;
            } else if (isProtected) {
                shouldReschedule = false;
                this.partialUpdateBatchDownload(itemId,
                    {
                        message: i18n.t(`${batchActionsNameSpace}.protectedDownloadFails`),
                        status: 'ERROR',
                    });
            } else if (failOn500Status) {
                shouldReschedule = false;
                this.partialUpdateBatchDownload(itemId,
                    {
                        message: i18n.t(`${batchActionsNameSpace}.downloadFails`),
                        status: 'ERROR',
                    });
            }
        }
        return shouldReschedule;
    }

    private async tryUpdateStatus(
        itemId: string,
        processStatusCheck: StatusCheckWorker,
        accessType: OpenOptions,
        failOn500Status = false,
        isProtected = false,
    ): Promise<void> {
        let shouldReschedule = true;
        try {
            if (accessType !== OpenOptions.download) {
                await this.retryOpenProtectedFileRequest(itemId, '', accessType);
            }
            if (this.downloadsMap.has(itemId)) {
                const { API } = this;

                const { name: filename, status } = this.downloadsMap.get(itemId);
                let result;
                if (isProtected) {
                    await this.retryOpenProtectedFileRequest(itemId, filename, accessType);
                } else {
                    result = await processStatusCheck(API, itemId, accessType);
                    const isMFARequired = result.message === 'Mfa required';
                    const isMFAPasswordRequired = result.message === 'Mfa password required';
                    if (isMFARequired || isMFAPasswordRequired) {
                        const { files, id } = result;
                        shouldReschedule = false;
                        this.remove(itemId);
                        this.setLastMFASource('batch');
                        if (isMFARequired) {
                            this.setIsMFAMounted(true);
                            this.setIsMFAVisible(true);
                        } else {
                            this.setIsMFAPasswordMounted(true);
                            this.setIsMFAPasswordVisible(true);
                        }
                        if (files && files.length) {
                            this.setMFARequiredFilesList([...files, ...this.MFARequiredFilesList]);
                        }
                        this.MFARequiredStatusUpdate(id, result.name);
                        this.processStatusCheckResult(itemId, result, accessType);
                    } else {
                        shouldReschedule = this.processStatusCheckResult(itemId, result, accessType);
                    }
                }
            }
        } catch (error) {
            shouldReschedule = this.processStatusCheckError(itemId, error, failOn500Status, isProtected);
        }
        if (shouldReschedule && !isProtected) {
            this.tryScheduleStatusCheck(itemId, processStatusCheck, accessType, failOn500Status, isProtected);
        }
    }

    private async retryOpenProtectedFileRequest(
        fileId: string,
        filename: string,
        accessType: OpenOptions = OpenOptions.download,
    ): Promise<DownloadActionResult> {
        const { API } = this.authSettingsStore;
        const result = await openFile({
            API,
            accessType,
            fileId,
            isProtected: true,
        });
        if (accessType === OpenOptions.download) {
            this.uploadProtectedFileResult(fileId, filename, (result as DownloadActionResult));
        } else {
            const { status, link } = (result as DownloadActionResult);
            if (this.downloadsMap.has(fileId) && status === 'success') {
                this.setBatchDownload(fileId, {
                    id: fileId,
                    name: filename,
                    status: 'DONE',
                });
            }
            if (status === 'success' && link) {
                const newWin = window.open(link);
                if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
                    window.location.href = link;
                }
            }
            this.API.cancelRequest(fileId);
            clearTimeout(this.processManagementMap.get(fileId));
        }

        return result as DownloadActionResult;
    }

    uploadProtectedFileResult = (fileId: string, filename: string, result: DownloadActionResult): void => {
        if (this.downloadsMap.has(fileId)) {
            this.setBatchDownload(fileId, {
                id: fileId,
                name: filename,
                status: 'DONE',
            });
        }
        this.processSuccessfulResult(fileId, result);
        this.API.cancelRequest(fileId);
        clearTimeout(this.processManagementMap.get(fileId));
    };

    MFARequiredStatusUpdate = (fileId: string, filename: string): void => {
        this.setBatchDownload(fileId, {
            id: fileId,
            name: filename,
            status: 'PROCESSING',
        });
    };

    startSingleProtectedDownloadStatusCheck = (
        fileId: string,
        filename: string,
        accessType: OpenOptions = OpenOptions.download,
    ): void => {
        this.setBatchDownload(fileId, {
            id: fileId,
            name: filename,
            status: 'PENDING',
        });
        this.tryUpdateStatus(fileId, getFileDownloadStatus, accessType, true, true);
    };

    startSingleDownloadStatusCheck = (fileId: string, filename: string, accessType: OpenOptions = OpenOptions.download): void => {
        this.setBatchDownload(fileId, {
            id: fileId,
            name: filename,
            status: 'PENDING',
        });
        this.tryUpdateStatus(fileId, getFileDownloadStatus, accessType, true, false);
    };

    startBatchDownloadStatusCheck = (batchId: string, name: string): void => {
        this.setBatchDownload(batchId, {
            id: batchId,
            name,
            status: 'PENDING',
        });
        this.tryUpdateStatus(batchId, getBatchDownloadStatus, OpenOptions.download, true, false);
    };

    updateBatchDownloadStatusWithError = (fileId: string, filename: string, errorMessage?: string): void => {
        this.setBatchDownload(fileId, {
            id: fileId,
            name: filename,
            status: 'ERROR',
            message: errorMessage,
        });
    };

    private partialUpdateBatchDownload(batchId: string, dataPartial: Partial<AsyncDownloadItem>): void {
        const batchDownload = this.downloadsMap.get(batchId);
        if (batchDownload) {
            this.setBatchDownload(batchId, { ...batchDownload, ...dataPartial });
        }
    }

    private watchOnDownloads(): void {
        this.downloadsMapReaction = reaction<boolean>(
            () => !!this.downloadsMap.size,
            (value) => this.setIsProgressWidgetOpen(value),
        );
    }
}

export default BatchDownloadStore;
